/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
import { call, put, takeEvery, select } from "redux-saga/effects";
import { getRequest, postRequest } from "../../helpers/Api";
import { types, actions } from "../reducers/qrCodeClockIn";

function* fetchQrCode({ payload: { id } }) {
  try {
    const response = yield call(getRequest, {
      url: `/qr-code/${id}`,
      authorized: false,
    });

    if (response.status === 404) {
      yield put(actions.fetchQrCodeFailed("QR code not found"));
      return;
    }
    if (response.status >= 400) {
      yield put(actions.fetchQrCodeFailed(response.message || "Something went wrong"));
      return;
    }

    yield put(actions.fetchQrCodeSucceeded(response));
  } catch (err) {
    yield put(actions.fetchQrCodeFailed(err.message || "Internal server error"));
  }
}

function* validateLocation({ payload: { location } }) {
  try {
    const { id } = yield select((state) => state.qrCodeClockIn.qrCode);
    const response = yield call(postRequest, {
      url: `/qr-code/${id}/contains-location`,
      authorized: false,
      jsonContentType: true,
      body: JSON.stringify(location),
    });

    if (response.status === 204) {
      const geo = yield call(getRequest, {
        url: `https://geolocation-db.com/json/`,
        fullUrl: true,
        authorized: false,
        jsonContentType: true,
      });
      yield put(
        actions.validateLocationSucceeded(
          geo && typeof geo === "object" && geo.IPv4 ? geo.IPv4 : null
        )
      );
    } else if (response.status >= 400) {
      yield put(actions.validateLocationFailed(response.message));
    }
  } catch (err) {
    yield put(actions.validateLocationFailed(err.message || "Internal server error"));
  }
}

function* clockInLogin({ payload: { data, onSuccess, onFailure } }) {
  try {
    const response = yield call(postRequest, {
      url: `/qr-code-clock-in/login`,
      authorized: false,
      jsonContentType: true,
      body: JSON.stringify(data),
    });

    if (response.status >= 200 && response.status < 300) {
      localStorage.setItem("QR_CLOCK_IN_TOKEN", response.token);
      yield put(actions.clockInLoginSucceeded(response.employee));
      onSuccess();
    } else if (response.status >= 400) {
      yield put(actions.clockInLoginFailed(response.message));
      onFailure();
    }
  } catch (err) {
    yield put(actions.clockInLoginFailed(err.message || "Internal server error"));
    onFailure();
  }
}

function* clockInCheckToken() {
  try {
    const token = localStorage.getItem("QR_CLOCK_IN_TOKEN");
    if (token) {
      const response = yield call(getRequest, {
        url: `/qr-code-clock-in/check-token`,
        authorized: false,
        jsonContentType: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status >= 200 && response.status < 300) {
        yield put(actions.clockInLoginSucceeded(response));
      } else if (response.status >= 400) {
        yield put(actions.clockInCheckTokenFailed());
        localStorage.removeItem("QR_CLOCK_IN_TOKEN");
      }
    } else {
      yield put(actions.clockInCheckTokenFailed());
    }
  } catch (err) {
    yield put(actions.clockInCheckTokenFailed());
  }
}

function* clockInLogout({ payload: { redirect } }) {
  try {
    if (redirect) {
      yield put(actions.clockInLogoutSucceeded());
      window.location.href = "/thank-you";
    } else {
      localStorage.removeItem("QR_CLOCK_IN_TOKEN");
      window.location.reload();
    }
  } catch (err) {
    yield put(actions.clockInLogoutFailed(err.message || "Internal server error"));
  }
}

function* checkIfClockedIn() {
  try {
    const { user, qrCode } = yield select((state) => state.qrCodeClockIn);
    const response = yield call(getRequest, {
      url: `/qr-code-clock-in/${qrCode.id}/${user.id}/shift`,
      jsonContentType: true,
      authorized: false,
    });
    if (response.status >= 200 && response.status < 300) {
      yield put(actions.checkIfClockedInSucceeded(response));
    } else if (response.status === 404 && response.message === "No shift found") {
      yield put(actions.checkIfClockedInSucceeded(null));
    } else if (response.status >= 400) {
      yield put(actions.checkIfClockedInFailed(response.message));
    }
  } catch (err) {
    yield put(actions.checkIfClockedInFailed(err.message || "Internal server error"));
  }
}

function* clockIn({ payload: { onSuccess } }) {
  try {
    const { user, qrCode, ip } = yield select((state) => state.qrCodeClockIn);
    const response = yield call(postRequest, {
      url: `/qr-code-clock-in/${qrCode.id}/${user.id}/shift/start`,
      authorized: false,
      jsonContentType: true,
      body: JSON.stringify({ ip }),
    });

    if (response.status >= 200 && response.status < 300) {
      yield put(actions.clockInSucceeded(response));
      if (onSuccess) {
        onSuccess();
      }
    } else if (response.status >= 400) {
      yield put(actions.clockInFailed(response.message));
    }
  } catch (err) {
    yield put(actions.clockInFailed(err.message || "Internal server error"));
  }
}

function* clockOut({ payload: { onSuccess, profileImage, signature } }) {
  try {
    const { user, qrCode, ip } = yield select((state) => state.qrCodeClockIn);
    const body = {};
    if (qrCode && qrCode.profileImage) {
      body.profileImage = profileImage;
    }
    if (qrCode && qrCode.signShift) {
      body.signature = signature;
    }
    if (qrCode && qrCode.collectIP) {
      body.ip = ip;
    }
    yield call(postRequest, {
      // backend was on pills
      url: `/qr-code-clock-in/${qrCode.id}/${user.id}/shift/stop`,
      authorized: false,
      jsonContentType: true,
      body: JSON.stringify(body),
    });

    yield put(actions.clockOutSucceeded());
    if (onSuccess) {
      onSuccess();
    }
  } catch (err) {
    yield put(actions.clockOutFailed(err.message || "Internal server error"));
  }
}

export default function* sagas() {
  yield takeEvery(types.REQUEST_FETCH_QR_CODE, fetchQrCode);
  yield takeEvery(types.REQUEST_VALIDATE_LOCATION, validateLocation);
  yield takeEvery(types.REQUEST_CLOCK_IN_LOGIN, clockInLogin);
  yield takeEvery(types.REQUEST_CLOCK_IN_CHECK_TOKEN, clockInCheckToken);
  yield takeEvery(types.REQUEST_CLOCK_IN_LOGOUT, clockInLogout);
  yield takeEvery(types.REQUEST_CHECK_IF_CLOCKED_IN, checkIfClockedIn);
  yield takeEvery(types.REQUEST_CLOCK_IN, clockIn);
  yield takeEvery(types.REQUEST_CLOCK_OUT, clockOut);
}
